/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {useReduxAuth} from '../useReduxAuth'
import Astericsign from '../../user/components/forms/Astericsign'
import {FaEye, FaEyeSlash} from 'react-icons/fa'
import {emailValidation} from '../../user/components/forms/helper/ValidationHelperComponent'
import MetaTags from '../../../meta/MetaTags'
import PrimaryButton from '../../user/components/common/buttons/PrimaryButton'

const loginSchema = Yup.object().shape({
  email: emailValidation,
  password: Yup.string()
    .min(8, 'Password length must be at least 8 characters long')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const {login: reduxLogin} = useReduxAuth()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus(null) // Clear any previous errors
      try {
        // Use Redux login instead of manual auth handling
        await reduxLogin(values)
        
        // After login, check intended URL in localStorage
        const intendedUrl = localStorage.getItem("intended_url")

        if (intendedUrl) {
          navigate(intendedUrl, { replace: true })
          localStorage.removeItem("intended_url")
        } else {
          navigate("/dashboard", { replace: true })
        }
        
      } catch (error: any) {
        console.error('Login failed:', error)
        
        // Extract error message from different possible error structures
        let errorMessage = 'Login failed. Please try again.'
        
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error?.message) {
          errorMessage = error.message
        } else if (typeof error === 'string') {
          errorMessage = error
        }
        
        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  // useState to handle password visible onclick eye icon
  const [passwordVisible, setPasswordVisible] = useState(false)
  const handleVisibility = () => {
    setPasswordVisible(!passwordVisible)
  }
  
  return (
    <>
      <MetaTags title={'Login'} />

      <form
        className='form w-100 '
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className='fw-bolder mb-3'>Sign In</h1>
        </div>

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder '>
            Email <Astericsign />
          </label>
          <input
            placeholder='Enter Email'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                // 'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder  fs-6 mb-0'>
            Password <Astericsign />
          </label>
          <div className='position-relative'>
            <input
              type={passwordVisible ? 'text' : 'password'}
              autoComplete='off'
              placeholder='Enter Password'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': (formik.touched.password || formik.values.password) && formik.errors.password,
                },
                {
                  // 'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            <span
              onClick={handleVisibility}
              className='cursor-pointer text-dark position-absolute end-0 top-50 translate-middle-y pe-5'
            >
              {passwordVisible ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {/* <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} /> */}
          {(formik.touched.password || formik.values.password) && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          {/* begin::Link */}
          <Link to='/forgot-password' className='link-primary'>
            <p className='text-black'>Forgot Password ?</p>
          </Link>
          {/* end::Link */}
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <PrimaryButton
            type='submit'
            id='kt_sign_in_submit'
            className='bg-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Sign In</span>}
            {loading && (
              <span className='indicator-progress d-block'>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </PrimaryButton>
        </div>
        {/* end::Action */}
      </form>
    </>
  )
}
